import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, AlertTriangle, Wrench, User, Calendar,
  Clock, DollarSign, CheckCircle, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { workOrdersApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime } from '@/lib/date';
import { Button } from '@/components/ui/button';
import { Badge, statusColor, priorityColor } from '@/components/ui/badge';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { AttachmentsPanel } from '@/components/AttachmentsPanel';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(mins: number | null | undefined) {
  if (!mins) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-600 font-medium uppercase tracking-wide">{label}</span>
      <span className={`text-sm text-slate-900 font-medium ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSupervisor, timezone } = useAuth();

  const [signOffOpen, setSignOffOpen] = useState(false);
  const [signOffNote, setSignOffNote] = useState('');
  const [signOffMeter, setSignOffMeter] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: wo, isLoading, error } = useQuery({
    queryKey: ['work-orders', 'detail', id],
    queryFn: () => workOrdersApi.get(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['work-orders'] });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  const signOff = useMutation({
    mutationFn: () => {
      const meter = signOffMeter.trim() ? Number(signOffMeter) : undefined;
      return workOrdersApi.signOff(id!, signOffNote, Number.isFinite(meter) ? meter : undefined);
    },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['equipment'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      setSignOffOpen(false); setSignOffNote(''); setSignOffMeter('');
    },
  });

  const approve = useMutation({
    mutationFn: () => workOrdersApi.approve(id!),
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: () => workOrdersApi.reject(id!, rejectReason),
    onSuccess: () => { invalidate(); setRejectOpen(false); setRejectReason(''); },
  });

  if (isLoading) {
    return <p className="text-center text-slate-400 py-16">Loading…</p>;
  }

  if (error || !wo) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-slate-400">
        <AlertTriangle size={32} />
        <p>Work order not found.</p>
        <Button variant="outline" onClick={() => navigate('/work-orders')}>
          <ArrowLeft size={14} /> Back to Work Orders
        </Button>
      </div>
    );
  }

  const canSignOff = wo.status === 'open' || wo.status === 'in_progress';
  const canApproveReject = isSupervisor && wo.status === 'awaiting_approval';

  const totalPartsCost = (wo.parts ?? []).reduce((sum, wp) => {
    const c = wp.part?.unit_cost ? Number(wp.part.unit_cost) : 0;
    return sum + c * wp.quantity_used;
  }, 0);

  return (
    <div className={cn('flex flex-col gap-5', (canSignOff || canApproveReject) && 'pb-20 sm:pb-0')}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/work-orders')}
          className="mt-1 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-slate-900 truncate">{wo.title}</h1>
            <Badge color={statusColor(wo.status)}>{wo.status.replace(/_/g, ' ')}</Badge>
            <Badge color={priorityColor(wo.priority)}>{wo.priority}</Badge>
          </div>
          <p className="text-sm text-slate-600 font-mono mt-0.5">{wo.wo_number}</p>
        </div>

        {/* Action buttons — desktop only; mobile uses sticky bar */}
        <div className="hidden sm:flex gap-2 shrink-0">
          {canSignOff && (
            <Button size="sm" onClick={() => setSignOffOpen(true)}>
              <CheckCircle size={14} /> Sign Off
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button size="sm" onClick={() => approve.mutate()} disabled={approve.isPending}>
                <ThumbsUp size={14} /> Approve
              </Button>
              <Button size="sm" variant="danger" onClick={() => setRejectOpen(true)}>
                <ThumbsDown size={14} /> Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Info cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Left: assignment & equipment */}
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label="Equipment"
                value={
                  wo.equipment ? (
                    <Link
                      to={`/equipment/${wo.equipment.id}`}
                      className="text-brand-600 hover:underline"
                    >
                      {wo.equipment.name}
                    </Link>
                  ) : wo.custom_equipment_name ? (
                    <span className="text-slate-600 italic">{wo.custom_equipment_name} <span className="text-xs text-slate-400 not-italic">(not in registry)</span></span>
                  ) : '—'
                }
              />
              <InfoRow label="Asset Tag" value={wo.equipment?.asset_tag ?? '—'} mono />
              <InfoRow label="Location" value={wo.equipment?.location ?? '—'} />
              <InfoRow
                label="Assigned Technician"
                value={
                  <span className="flex items-center gap-1">
                    <User size={13} className="text-slate-400" />
                    {wo.assigned_technician?.full_name ?? 'Unassigned'}
                  </span>
                }
              />
              {wo.maintenance_task && (
                <InfoRow
                  label="Linked Task"
                  value={
                    <span className="flex items-center gap-1">
                      <Wrench size={13} className="text-slate-400" />
                      {wo.maintenance_task.name}
                    </span>
                  }
                />
              )}
              <InfoRow
                label="Source"
                value={(() => {
                  // How this WO came to exist:
                  //   - meter_pm  : auto-created by a meter-based PM crossing its threshold
                  //   - time_pm   : auto-created by a time-based PM at the scheduled time
                  //   - manual    : someone clicked "New Work Order"
                  if (wo.trigger_meter_value != null) {
                    const unit = wo.equipment?.meter_unit ?? '';
                    return (
                      <span className="text-slate-700">
                        Auto-generated · meter reading{' '}
                        <span className="font-mono font-semibold">
                          {Number(wo.trigger_meter_value).toLocaleString()}{unit ? ` ${unit}` : ''}
                        </span>
                      </span>
                    );
                  }
                  if (wo.maintenance_task) {
                    return <span className="text-slate-700">Auto-generated from PM schedule</span>;
                  }
                  return <span className="text-slate-700">Manually created</span>;
                })()}
              />
            </div>
          </CardBody>
        </Card>

        {/* Right: schedule & actuals */}
        <Card>
          <CardHeader title="Schedule & Time" />
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label="Scheduled Start"
                value={
                  <span className="flex items-center gap-1">
                    <Calendar size={13} className="text-slate-400" />
                    {fmtDateTime(wo.scheduled_start, timezone)}
                  </span>
                }
              />
              <InfoRow label="Scheduled End" value={fmtDateTime(wo.scheduled_end, timezone)} />
              <InfoRow label="Actual Start" value={fmtDateTime(wo.actual_start, timezone)} />
              <InfoRow label="Actual End" value={fmtDateTime(wo.actual_end, timezone)} />
              <InfoRow
                label="Labor Hours"
                value={
                  <span className="flex items-center gap-1">
                    <Clock size={13} className="text-slate-400" />
                    {wo.labor_hours != null ? `${wo.labor_hours}h` : '—'}
                  </span>
                }
              />
              <InfoRow
                label="Downtime"
                value={
                  <span className={wo.downtime_minutes ? 'text-red-600' : ''}>
                    {fmtMins(wo.downtime_minutes)}
                  </span>
                }
              />
              <InfoRow
                label="Total Cost"
                value={
                  <span className="flex items-center gap-1">
                    <DollarSign size={13} className="text-slate-400" />
                    {wo.cost != null ? `$${Number(wo.cost).toLocaleString()}` : '—'}
                  </span>
                }
              />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* ── Description & completion notes ──────────────────────────────────── */}
      {(wo.description || wo.completion_notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {wo.description && (
            <Card>
              <CardHeader title="Description" />
              <CardBody>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{wo.description}</p>
              </CardBody>
            </Card>
          )}
          {wo.completion_notes && (
            <Card>
              <CardHeader title="Completion Notes" />
              <CardBody>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{wo.completion_notes}</p>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* ── Parts used ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={`Parts Used (${(wo.parts ?? []).length})`} />
        <CardBody className="p-0">
          {(wo.parts ?? []).length === 0 ? (
            <p className="px-5 py-6 text-center text-slate-400 text-sm">No parts recorded.</p>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Part #</Th>
                  <Th>Name</Th>
                  <Th className="text-right">Qty</Th>
                  <Th className="text-right">Unit Cost</Th>
                  <Th className="text-right">Subtotal</Th>
                </tr>
              </Thead>
              <Tbody>
                {(wo.parts ?? []).map(wp => {
                  const cost = wp.part?.unit_cost ? Number(wp.part.unit_cost) : null;
                  return (
                    <Tr key={wp.part_id}>
                      <Td className="font-mono text-xs">{wp.part?.part_number ?? wp.part_id}</Td>
                      <Td className="font-medium text-sm">{wp.part?.name ?? '—'}</Td>
                      <Td className="text-right">{wp.quantity_used}</Td>
                      <Td className="text-right text-sm">
                        {cost !== null ? `$${cost.toFixed(2)}` : '—'}
                      </Td>
                      <Td className="text-right text-sm font-medium">
                        {cost !== null ? `$${(cost * wp.quantity_used).toFixed(2)}` : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
              {totalPartsCost > 0 && (
                <tfoot>
                  <tr className="border-t bg-slate-50">
                    <td colSpan={4} className="px-4 py-2 text-right text-sm font-semibold text-slate-600">
                      Total Parts Cost
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-slate-900">
                      ${totalPartsCost.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          )}
        </CardBody>
      </Card>

      {/* ── Sign-off dialog ─────────────────────────────────────────────────── */}
      <Dialog open={signOffOpen} onClose={() => setSignOffOpen(false)} title="Sign Off Work Order">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-slate-600">
            Submitting will move this work order to "Awaiting Approval" until a supervisor approves it.
          </p>
          <Textarea
            label="Completion Note"
            value={signOffNote}
            onChange={e => setSignOffNote(e.target.value)}
            placeholder="What was done, parts used, observations…"
          />

          {/* Meter reading at completion — only relevant for meter-based PMs.
              When provided, the backend logs a meter reading on the equipment
              AND advances the task's trigger point so the next interval counts
              from now (instead of from the original threshold-crossing). */}
          {wo.maintenance_task?.schedule_type === 'meter' && (
            <div className="rounded-lg border border-brand-100 bg-brand-50/60 p-3 text-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-700 mb-1">
                Meter reading at completion
              </p>
              <p className="text-xs text-slate-600 mb-2">
                Optional. If you serviced this {wo.equipment?.meter_unit ?? 'meter'} early or late, enter the actual reading now and the next PM will count from this point instead of the original trigger.
                {wo.equipment?.current_reading != null && (
                  <> Current: <span className="font-mono font-medium">{Number(wo.equipment.current_reading).toLocaleString()}{wo.equipment.meter_unit ? ` ${wo.equipment.meter_unit}` : ''}</span>.</>
                )}
              </p>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={signOffMeter}
                onChange={e => setSignOffMeter(e.target.value)}
                placeholder={wo.equipment?.current_reading != null ? String(wo.equipment.current_reading) : '0'}
                className="w-full rounded-md border border-canvas-line bg-white px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          )}

          {signOff.error && <p className="text-sm text-red-600">{(signOff.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setSignOffOpen(false)}>Cancel</Button>
            <Button onClick={() => signOff.mutate()} disabled={signOff.isPending}>
              {signOff.isPending ? 'Submitting…' : 'Submit Sign-off'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* ── Reject dialog ───────────────────────────────────────────────────── */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject Work Order">
        <div className="flex flex-col gap-3">
          <Textarea
            label="Rejection Reason"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain why this is being rejected…"
          />
          {reject.error && <p className="text-sm text-red-600">{(reject.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending}>
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* ── Attachments ────────────────────────────────────────────────────── */}
      <AttachmentsPanel entityType="work_order" entityId={id!} />

      {/* ── Sticky mobile action bar ─────────────────────────────────────────── */}
      {(canSignOff || canApproveReject) && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 p-3 flex gap-2">
          {canSignOff && (
            <Button
              className="flex-1 py-3 text-base justify-center"
              onClick={() => setSignOffOpen(true)}
            >
              <CheckCircle size={16} /> Sign Off
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                className="flex-1 py-3 text-base justify-center"
                onClick={() => approve.mutate()}
                disabled={approve.isPending}
              >
                <ThumbsUp size={16} /> Approve
              </Button>
              <Button
                variant="danger"
                className="flex-1 py-3 text-base justify-center"
                onClick={() => setRejectOpen(true)}
              >
                <ThumbsDown size={16} /> Reject
              </Button>
            </>
          )}
        </div>
      )}

    </div>
  );
}
